
import { 
  doc, 
  collection, 
  addDoc,
  updateDoc,
  serverTimestamp, 
  Firestore,
  Timestamp
} from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import { getLatestLocationVersion } from "./versionStatusService";
import { processLocationDataForStorage } from "./versionDataProcessingService";

/**
 * Creates a new version of a location
 */
export const createLocationVersion = async (
  db: Firestore,
  locationData: CampLocation,
  userId: string,
  action: string = 'update',
  changeDescription: string = '',
  status: 'draft' | 'published' | 'archived' = 'draft',
  customTimestamp?: Timestamp,
  isNamedVersion: boolean = false
) => {
  try {
    console.log(`[versionCreationService] Creating version with params: action=${action}, userId=${userId}, status=${status}, isNamedVersion=${isNamedVersion}, changeDesc=${changeDescription}`);
    
    const locationRef = doc(db, "campLocations", locationData.id);
    const userRef = userId !== "system" ? doc(db, "users", userId) : null;
    
    // Get the latest version to determine the next version number
    // This ensures we always increment by exactly 1 from the latest draft
    const latestVersion = await getLatestLocationVersion(db, locationData.id);
    
    // Get the latest version number (or 0 if no versions exist)
    const latestVersionNumber = latestVersion?.versionNumber || 0;
    
    // Next version is always latestVersionNumber + 1
    const nextVersionNumber = latestVersionNumber + 1;
    
    console.log(`[versionCreationService] Latest version number: ${latestVersionNumber}, next version number will be: ${nextVersionNumber}`);
    
    // Process the location data for storage (clean and format)
    const cleanedData = await processLocationDataForStorage(db, locationData);
    
    // Create a new version document with version number
    const versionData = {
      locationRef: locationRef,
      data: cleanedData,
      timestamp: customTimestamp || serverTimestamp(),
      user: userRef,
      action: action,
      changeDescription: changeDescription,
      status: status,
      versionNumber: nextVersionNumber,
      isNamedVersion: isNamedVersion
    };
    
    const versionRef = await addDoc(collection(db, "campLocationVersions"), versionData);
    console.log(`[versionCreationService] Created new location version ${nextVersionNumber}: ${versionRef.id}`);
    
    // Update the location document to reference the latest version
    await updateVersionReference(db, locationData.id, versionRef.id);
    
    return versionRef.id;
  } catch (error) {
    console.error("[versionCreationService] Error creating location version:", error);
    throw error;
  }
};

/**
 * Updates the location document to reference the latest version
 */
const updateVersionReference = async (
  db: Firestore,
  locationId: string,
  versionId: string
) => {
  const locationRef = doc(db, "campLocations", locationId);
  await updateDoc(locationRef, {
    latestVersionId: versionId
  });
  
  console.log(`[versionCreationService] Updated location ${locationId} to reference version ${versionId}`);
  return true;
};
